import type { StatementHeader, StatementKind, StatementLine } from '../api/types'
import { partialOf } from '../api/answer'

/**
 * THE STATEMENT OF ACCOUNT AS A DOCUMENT — the light half, shared by the
 * screen and the two files.
 *
 * Every other report extracts through model.ts, where a file is the SCREEN
 * re-typeset: eyebrow, filters, "viewing as", the dash footnote. A statement is
 * not that. It is addressed to a CUSTOMER, so it carries none of those internal
 * lines, and it has what an extract never has: a letterhead and an addressee.
 * Hence its own model and its own generators (statementPdf.ts,
 * statementExcel.ts), which this file must never import. The page imports this
 * eagerly; jsPDF, exceljs and the fonts load only when somebody downloads.
 *
 * VERSION 3 (6 Oct 2026): THE DATA OF D365'S OWN ACCOUNT STATEMENT, IN THE
 * PLATFORM'S DESIGN. The owner asked for everything D365's statement header
 * prints -- the company's address, telephone and fax; the customer's address
 * and account; terms, currency, period, credit limit, credit available; the
 * date and time and the page -- without the tax number, and for the layout to
 * be ours rather than D365's. The lines are the D365 team's templates': Date,
 * Salesperson, Document, Sales order, Reference, Due, Days overdue, [Original
 * amount,] Debit, Credit and Balance, between an opening and a closing balance
 * per currency.
 *
 * ONE SET OF RULES FOR SCREEN AND PAPER, so the two cannot disagree:
 *   - amounts to two decimals, always (measured 6 Oct 2026: no amount on the
 *     ledger carries a third), with a true minus sign, which in IBM Plex is
 *     exactly one digit wide, so a column of balances stays aligned;
 *   - dates as the platform writes them, "02 Jan 2026": one form for a reader
 *     in Riyadh and one in Manila, neither of whom reads 01/02 the same way;
 *   - the side of a line with no amount is left EMPTY, as an accounts ledger
 *     prints it; a dash is reserved for one meaning, as on every MBI screen:
 *   - a null FIGURE is a dash: the column access withheld it. Never a zero.
 */

export const DASH = '—'
const MINUS = '−'

/** Everything a statement file is made from. */
export interface StatementDoc {
  headers: StatementHeader[]
  lines: StatementLine[]
}

/** One currency's part of a company's statement: its opening row, its lines, its closing row. */
export interface StatementSection {
  currency: string
  /** The company's own currency, which the procedure lists first, as D365 does. */
  home: boolean
  opening: StatementLine | null
  lines: StatementLine[]
  closing: StatementLine | null
}

/** One company's statement: its letterhead and its currency sections. */
export interface CompanyStatement {
  header: StatementHeader
  sections: StatementSection[]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "02 Jan 2026" from the API's "2026-01-02T00:00:00". Read as a CALENDAR date,
 * never through a time zone: `new Date()` on that string is local midnight,
 * which a browser west of Greenwich would happily print as the day before.
 */
export function docDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return ''
  return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`
}

/** "2026-10-06": the date part, for date inputs, requests and file names. */
export function isoDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : ''
}

/** The calendar date for an Excel cell: UTC midnight, so the serial number is the date and nothing else. */
export function excelDate(iso: string | null | undefined): Date | null {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null
}

/** "01 Jan 2026 – 06 Oct 2026". */
export function periodText(h: StatementHeader): string {
  return `${docDate(h.fromDate)} – ${docDate(h.toDate)}`
}

/**
 * "06 Oct 2026, 14:52": when this copy was made. D365's statement prints the
 * date and time too, so a customer holding two copies can tell which is newer.
 * The reader's clock, because it is the moment THEY produced it.
 */
export function preparedAt(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** 1,234.50 — two decimals, always; a true minus. Null is the dash. */
export function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return DASH
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  // -0.004 rounds to 0.00, and "−0.00" is a figure nobody can explain.
  return n < 0 && s !== '0.00' ? `${MINUS}${s}` : s
}

/**
 * A line's Debit or Credit: EMPTY where the line has none (a payment's debit),
 * the dash where the amounts are withheld.
 */
export function sideAmount(n: number | null, withheld: boolean): string {
  if (withheld) return DASH
  return n === null || n === 0 ? '' : money(n)
}

/** Does this text need the Arabic face? Letters, presentation forms, Arabic-Indic digits. */
export function hasArabic(s: string | null | undefined): boolean {
  return !!s && /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(s)
}

/**
 * An address as lines to print. D365 sends its address as one text, lines
 * split by a line feed, and measured 6 Oct 2026, 11,163 of 11,339 customer
 * addresses carry a "%1" -- a slot D365's address format left unfilled, almost
 * always as ",%1" after the city ("Dubai,%1"). It is dropped with the comma
 * that introduced it; empty lines go; nothing else is touched, so the address
 * reads exactly as D365 holds it. Company addresses carry no such token.
 */
export function addressLines(raw: string | null | undefined): string[] {
  if (!raw) return []
  return raw
    .split(/\r?\n/)
    .map((l) => l.replace(/\s*,?\s*%\d+/g, '').replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0)
}

/** The company's telephone and fax, those D365 holds: 14 and 7 of 41 companies. */
export function contacts(h: StatementHeader): { label: string; value: string }[] {
  return [
    ...(h.companyPhone ? [{ label: 'Tel', value: h.companyPhone }] : []),
    ...(h.companyFax ? [{ label: 'Fax', value: h.companyFax }] : []),
  ]
}

/** The statement as companies, each with its sections, in the order the procedure sent them. */
export function companiesOf(doc: StatementDoc): CompanyStatement[] {
  return doc.headers.map((header) => {
    const sections: StatementSection[] = []
    const at = new Map<string, StatementSection>()
    for (const l of doc.lines) {
      if ((l.company ?? '') !== (header.company ?? '')) continue
      const code = l.currency ?? ''
      let s = at.get(code)
      if (!s) {
        s = { currency: code, home: code === header.homeCurrency, opening: null, lines: [], closing: null }
        at.set(code, s)
        sections.push(s)
      }
      if (l.rowKind === 'opening') s.opening = l
      else if (l.rowKind === 'closing') s.closing = l
      else s.lines.push(l)
    }
    return { header, sections }
  })
}

/** The currencies a company's statement runs in, its own first; its own alone when there are no lines. */
export function currenciesOf(c: CompanyStatement): string[] {
  return c.sections.length > 0 ? c.sections.map((s) => s.currency) : [c.header.homeCurrency ?? '']
}

/** One currency's closing figure, for the balance panel. */
export interface Closing {
  currency: string
  balance: number | null
}

/** The closing balance of every currency: 0.00 in the company's own when the period had nothing. */
export function closingsOf(c: CompanyStatement): Closing[] {
  if (c.sections.length === 0) return [{ currency: c.header.homeCurrency ?? '', balance: 0 }]
  return c.sections.map((s) => ({ currency: s.currency, balance: s.closing?.balance ?? null }))
}

/**
 * How much of the credit limit the balance at To used: for the meter beside
 * credit available. None when no limit is set (0.00: 5,205 of 11,339
 * customers) or either figure is withheld. The balance is the limit minus
 * what is available, both in the company's own currency, as 043 works it.
 */
export function creditUse(h: StatementHeader): { share: number; over: boolean } | null {
  if (h.creditLimit === null || h.creditAvailable === null || h.creditLimit <= 0) return null
  const used = h.creditLimit - h.creditAvailable
  return { share: Math.max(0, Math.min(1, used / h.creditLimit)), over: h.creditAvailable < 0 }
}

/**
 * Were the amounts withheld? Every row carries a balance whenever the amount
 * column is visible — the opening row, each line, the closing row — so a row
 * without one can only be the mask. Debit and credit cannot answer this: a
 * payment has no debit and an invoice no credit, by design.
 *
 * UNDER THE NEW MODEL a balance is blank only when every line behind it is
 * hidden; once one shows, the balances are numbers that leave the hidden lines
 * out, and Partial says so (056). Such a statement understates what the customer
 * owes, so it is withheld as a whole too (16c's review): a document for the
 * customer is whole or not at all. Partial is never set under today's model.
 */
export function amountsWithheld(lines: StatementLine[]): boolean {
  return lines.some((l) => l.balance === null || partialOf(l).size > 0)
}

/** "Full statement" / "Open items". */
export function kindLabel(kind: StatementKind): string {
  return kind === 'open' ? 'Open items' : 'Full statement'
}

/**
 * What the Open statement lists is what is unpaid TODAY, whatever the To date
 * (the owner's decision 3): the data keeps no history of payment matching.
 */
export function openNote(h: StatementHeader): string {
  return `Unpaid as of ${docDate(h.today)}`
}

/** Beside "Opening balance": what it carries in. */
export function openingNote(h: StatementHeader): string {
  return `before ${docDate(h.fromDate)}`
}

/** Beside "Closing balance": when it stands. Open's is today's unpaid, whatever the To date. */
export function closingNote(h: StatementHeader): string {
  return h.kind === 'open' ? `unpaid as of ${docDate(h.today)}` : `at ${docDate(h.toDate)}`
}

/** The columns, in order, as the screen, the PDF and the Excel all print them. */
export interface DocColumn {
  key: 'date' | 'salesMan' | 'document' | 'salesOrder' | 'reference' | 'due' | 'overdueDays'
    | 'originalAmount' | 'debit' | 'credit' | 'balance'
  header: string
  /** Figures sit on a shared right edge. */
  figure?: boolean
}

export function columnsFor(kind: StatementKind): DocColumn[] {
  return [
    { key: 'date', header: 'Date' },
    { key: 'salesMan', header: 'Salesperson' },
    { key: 'document', header: 'Document no.' },
    { key: 'salesOrder', header: 'Sales order' },
    { key: 'reference', header: 'Reference' },
    { key: 'due', header: 'Due date' },
    { key: 'overdueDays', header: 'Days overdue' },
    ...(kind === 'open' ? [{ key: 'originalAmount' as const, header: 'Original amount', figure: true }] : []),
    { key: 'debit', header: 'Debit', figure: true },
    { key: 'credit', header: 'Credit', figure: true },
    { key: 'balance', header: 'Balance', figure: true },
  ]
}

/**
 * "statement-full-C-1001-2026-12-31.pdf": the statement, the account and
 * the To date, which is what a reader files it by. One file per download
 * whatever the number of companies; the first company's account names it.
 */
export function statementFileName(doc: StatementDoc, ext: 'pdf' | 'xlsx'): string {
  const h = doc.headers[0]
  // Measured 6 Oct 2026: 128 ledger rows carry an account with a space or a
  // bracket in it ("GEN SCRAP", "ATlC(EL)001").
  const account =
    (h?.customerAccount ?? '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'customer'
  return `statement-${h?.kind ?? 'full'}-${account}-${isoDate(h?.toDate)}.${ext}`
}
